import { UserState } from '@store/reducers/userData';
import { useState } from 'react';
import storeConnector from '@store/storeConnector';
import { Alert, AlertEntity, HistoricalAlert } from '@src/types/Alerts';
import useAlertMutations from '@src/hooks/api/mutations/useAlertMutations';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Actions } from '@src/types/Actions';
import { isActionRole } from '@src/services/auth';
import alertsx from '@components/_shared/Alerts/AlertsSx';
import useAlias from '@alias';
import FormattedAlias from '@components/_shared/Translations/FormattedAlias';

function AcknowledgeCell({
  alertEntity,
  actions,
  username,
  role,
}: {
  alertEntity: Alert;
  actions: Actions;
  username: string;
  role: UserState['role'];
}) {
  const alias = useAlias();
  const [isUpdating, setIsUpdating] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [ackedNote, setAckedNote] = useState('');

  const { acknowledgeAlert } = useAlertMutations();

  const alertTopic = (alertEntity as HistoricalAlert).topic;
  const alertName =
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    (alertEntity as AlertEntity).config?.alertName ||
    (alertEntity as HistoricalAlert).configAlertName;

  const handleAcknowledge = async () => {
    if (!ackedNote) {
      return;
    }

    setModalOpen(false);
    setIsUpdating(true);

    acknowledgeAlert.mutate(
      {
        alertTopics: [alertTopic],
        ackedBy: username,
        ackedNote,
      },
      {
        onError: async (e) => {
          actions.notifyError(`Failed to acknowledge alarm - ${e}`);
        },
        onSettled: async () => {
          setIsUpdating(false);
        },
      },
    );
  };

  if (!isActionRole(role)) {
    return null;
  }

  if (alertEntity.isAcked) {
    return (
      <Tooltip title={(alertEntity as AlertEntity).ackedNote} placement='left'>
        <Box sx={alertsx.alertCenterAlignBox}>
          <Typography>
            {(alertEntity as AlertEntity).ackedBy ?? (
              <FormattedAlias id='Acknowledged' />
            )}
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  if (isUpdating) {
    return <CircularProgress size={25} />;
  }
  return (
    <>
      <Button
        onClick={() => setModalOpen(true)}
        size='small'
        sx={{ textTransform: 'none' }}
      >
        <FormattedAlias id='Acknowledge' />
      </Button>
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth='sm'
        fullWidth
      >
        <DialogTitle>
          <FormattedAlias id='Acknowledge' />: {alertName}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            label={alias('Leave a note...')}
            type='text'
            fullWidth
            variant='standard'
            value={ackedNote}
            onChange={(e) => setAckedNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions
          sx={{ display: 'flex', justifyContent: 'space-between' }}
        >
          <Button onClick={() => setModalOpen(false)} variant='outlined'>
            <FormattedAlias id='Cancel' />
          </Button>
          <Button onClick={handleAcknowledge} variant='contained'>
            <FormattedAlias id='Confirm' />
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default storeConnector(AcknowledgeCell, {
  user: ['username', 'role'],
});
